import React, { useEffect } from 'react';
import { Row, Col, Form, Skeleton } from 'antd';
import { Title } from '@/components/Typography';
import { RiskLevelCard } from './RiskLevelCard';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useEwaJdsD86Context } from '@/components/views/EwaJdsD86/context';
import { Vulnerabilities, Exposures, Severities, RiskCategories, RiskDescriptions, RiskDamages } from './Inputs';
import { GetCustomReportResultStepKeyDefaultDataResponse } from '@/hooks/useGetCustomReportResultStepKeyDefaultData';
import {
	useCalculateStepKeyRpn,
	useGetCustomReportStepKeysDefaultRisks,
	useGetCustomReportSubStepKeysDefaultRisks
} from '@/hooks';

interface DefaultSelectsProps {
	stepKeyId: string;
	subStepKeyId?: string;
	formFieldName: string[];
	disableExposure?: boolean;
	children?: React.ReactNode;
	gettingCustomReportDefaultData?: boolean;
	customReportDefaultData?: GetCustomReportResultStepKeyDefaultDataResponse | null;
}

const { useWatch, useFormInstance } = Form;

export function DefaultSelects({
	children,
	stepKeyId,
	subStepKeyId,
	formFieldName,
	disableExposure = false,
	customReportDefaultData,
	gettingCustomReportDefaultData
}: Readonly<DefaultSelectsProps>)  {
	const form = useFormInstance();
	const { ewaJdsD86 } = useEwaJdsD86Context();
	const { organization, company } = useApplicationContext();

	const severityId: string = useWatch([...formFieldName, 'severity_id'], form);
	const exposureId: string = useWatch([...formFieldName, 'exposure_id'], form);
	const vulnerabilityId: string = useWatch([...formFieldName, 'vulnerability_id'], form);

	const { isFetching: gettingStepKeyDefaultRisks, data: stepKeyDefaultRisks } =
		useGetCustomReportStepKeysDefaultRisks({
			organization_id: organization?.id,
			company_id: company?.id,
			custom_report_step_key_id: stepKeyId
		});

	const { isFetching: gettingSubStepKeyDefaultRisks, data: subStepKeyDefaultRisks } =
		useGetCustomReportSubStepKeysDefaultRisks({
			organization_id: organization?.id,
			company_id: company?.id,
			custom_report_sub_step_key_id: subStepKeyId
		});

	const { isFetching: gettingRpnScore, data: rpnScore } = useCalculateStepKeyRpn({
		organization_id: organization?.id,
		company_id: company?.id,
		exposure_id: exposureId,
		severity_id: severityId,
		vulnerability_id: vulnerabilityId,
		custom_report_step_key_id: stepKeyId
	});

	useEffect(() => {
		if (
			(!gettingCustomReportDefaultData && customReportDefaultData) ||
			(!gettingStepKeyDefaultRisks && !gettingCustomReportDefaultData && stepKeyDefaultRisks) ||
			(!gettingSubStepKeyDefaultRisks && !gettingCustomReportDefaultData && subStepKeyDefaultRisks)
		) {
			const defaultData = subStepKeyId
				? customReportDefaultData?.sub_step_key_results?.find((f) => f?.sub_step_key_id === subStepKeyId)
				: customReportDefaultData?.step_key_result;

			const riskCategoryId: string = form.getFieldValue([...formFieldName, 'risk_category_id']);

			if (!riskCategoryId) {
				form.setFieldValue(
					[...formFieldName, 'risk_category_id'],
					defaultData?.risk_category_id ??
						subStepKeyDefaultRisks.risk_category_id ??
						stepKeyDefaultRisks.risk_category_id
				);
			}

			const riskDescriptionId: string = form.getFieldValue([...formFieldName, 'risk_description_id']);

			if (!riskDescriptionId) {
				form.setFieldValue(
					[...formFieldName, 'risk_description_id'],
					defaultData?.risk_description_id ??
						subStepKeyDefaultRisks.risk_description_id ??
						stepKeyDefaultRisks.risk_description_id
				);
			}

			const riskDamageId: string = form.getFieldValue([...formFieldName, 'risk_damage_id']);

			if (!riskDamageId) {
				form.setFieldValue(
					[...formFieldName, 'risk_damage_id'],
					defaultData?.risk_damage_id ??
						subStepKeyDefaultRisks.risk_damage_id ??
						stepKeyDefaultRisks.risk_damage_id
				);
			}

			const exposureId: string = form.getFieldValue([...formFieldName, 'exposure_id']);

			if (!exposureId) {
				form.setFieldValue([...formFieldName, 'exposure_id'], defaultData?.exposure_id);
			}

			const severityId: string = form.getFieldValue([...formFieldName, 'severity_id']);

			if (!severityId) {
				form.setFieldValue([...formFieldName, 'severity_id'], defaultData?.severity_id);
			}

			const vulnerabilityId: string = form.getFieldValue([...formFieldName, 'vulnerability_id']);

			if (!vulnerabilityId) {
				form.setFieldValue([...formFieldName, 'vulnerability_id'], defaultData?.vulnerability_id);
			}
		}
	}, [gettingStepKeyDefaultRisks, gettingSubStepKeyDefaultRisks, gettingCustomReportDefaultData]);

	useEffect(() => {
		if (!gettingRpnScore && !gettingCustomReportDefaultData && severityId) {
			let rpn = {
				score: (rpnScore?.score ?? customReportDefaultData?.step_key_result?.score) as number,
				result: (rpnScore?.result ?? customReportDefaultData?.step_key_result?.result) as number
			};

			// let rpn = {
			// 	result: customReportDefaultData?.step_key_result?.result ?? rpnScore.result,
			// 	score: customReportDefaultData?.step_key_result?.score ?? rpnScore.score
			// };

			if (subStepKeyId && customReportDefaultData) {
				rpn.result =
					rpn.result ??
					customReportDefaultData?.sub_step_key_results?.find((f) => f?.sub_step_key_id === subStepKeyId)
						?.result;
				rpn.score =
					rpn.score ??
					customReportDefaultData?.sub_step_key_results?.find((f) => f?.sub_step_key_id === subStepKeyId)
						?.score;
			}

			form.setFieldValue([...formFieldName, 'result'], rpn.result);
			form.setFieldValue([...formFieldName, 'score'], rpn.score);
		}
	}, [gettingRpnScore, gettingCustomReportDefaultData]);

	// useEffect(() => {
	// 	if (!gettingRpnScore && !gettingCustomReportDefaultData && severityId) {
	// 		let rpn = {
	// 			result: customReportDefaultData?.step_key_result?.result ?? rpnScore.result,
	// 			score: customReportDefaultData?.step_key_result?.score ?? rpnScore.score
	// 		};
	// 		if (subStepKeyId && customReportDefaultData) {
	// 			rpn.result =
	// 				customReportDefaultData?.sub_step_key_results?.find((f) => f?.sub_step_key_id === subStepKeyId)
	// 					?.result ?? rpn.result;
	// 			rpn.score =
	// 				customReportDefaultData?.sub_step_key_results?.find((f) => f?.sub_step_key_id === subStepKeyId)
	// 					?.score ?? rpn.score;
	// 		}

	// 		form.setFieldValue([...formFieldName, 'result'], rpn.result);
	// 		form.setFieldValue([...formFieldName, 'score'], rpn.score);
	// 	}
	// }, [gettingRpnScore, gettingCustomReportDefaultData]);


	return (
		<Row gutter={[0, 50]}>
			<Col sm={24} lg={12}>
				<Row justify="start">
					<Col sm={24} lg={18}>
						<RiskCategories formFieldName={formFieldName} />
						<RiskDescriptions formFieldName={formFieldName} />
						<RiskDamages formFieldName={formFieldName} />
					</Col>
				</Row>
			</Col>
			<Col sm={24} lg={12}>
				<Row justify="start">
					<Col sm={24} lg={18}>
						<Severities formFieldName={formFieldName} />
						<Vulnerabilities formFieldName={formFieldName} />
						<Exposures formFieldName={formFieldName} disabled={disableExposure} />
					</Col>
				</Row>
			</Col>
			<Col sm={24} lg={12}>
				{children}
			</Col>
			{gettingRpnScore ? (
				<Col sm={24} lg={12}>
					<Row gutter={[0, 10]}>
						<Col span={24}>
							<Skeleton active />
						</Col>
					</Row>
				</Col>
			) : (
				<Col sm={24} lg={12}>
					<Row gutter={[0, 10]}>
						<Col span={24}>
							<Title level={4}>Risk Index:</Title>
						</Col>
						<Col span={24}>
							<RiskLevelCard
								stepKeyId={stepKeyId}
								score={rpnScore.score}
								value={rpnScore.result}
								fileId={ewaJdsD86.file_id}
								subStepKeyId={subStepKeyId}
							/>
						</Col>
					</Row>
				</Col>
			)}
		</Row>
	);
}
